import { Component, OnInit, OnDestroy, inject, signal, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, Platform } from '@ionic/angular';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { Util } from 'src/shared/util/util';

import { SteadilyCoverageDetails, SteadilyEstimate } from 'src/models/steadily.model';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

export interface SteadilyQuoteParams {
  estimate: SteadilyEstimate;
  propertyName: string;
  addressLine?: string;
  currentProvider?: string;
  /** Current policy's annual premium in dollars, for the savings comparison. */
  currentAnnualPremium?: number;
}

interface QuotePlanView {
  product: string;
  name: string;
  description: string;
  monthly: string;
  annual: string;
  isRecommended: boolean;
  coverageLines: { label: string; value: string }[];
}

interface ComparisonBar {
  label: string;
  amountLabel: string;
  /** Fill width as a percentage of the more expensive option. */
  widthPct: number;
  tone: 'current' | 'steadily';
}

const PLAN_NAMES: Record<string, { name: string; description: string }> = {
  DP1: { name: 'Basic', description: 'Named perils · actual cash value' },
  DP2: { name: 'Broad', description: 'Broad form coverage' },
  DP3: { name: 'Special', description: 'Open perils · replacement cost' },
};

/** Give the embedded funnel this long before suggesting the full-tab fallback. */
const EMBED_HINT_DELAY_MS = 8000;

/**
 * In-app Steadily quote: animated premium summary, current-vs-quote comparison,
 * plan details, and the co-branded application embedded inline. The prefilled
 * `start_url` funnel carries the referral attribution, so every path out of this
 * page goes through it.
 */
@Component({
  templateUrl: 'steadily-quote.html',
  styleUrls: ['steadily-quote.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SteadilyQuotePage implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private sanitizer = inject(DomSanitizer);
  private windowService = inject(WindowService);
  private platform = inject(Platform);

  /** Running inside the Capacitor/Cordova native shell (iOS/Android), not the web. */
  readonly isNativeApp = this.platform.is('hybrid');

  /** Populated directly by the right-side dialog container (`componentRef.instance.data`). */
  data: SteadilyQuoteParams | null = null;

  loaded = false;
  // First-render guess before the async storage read resolves: desktop opens in the panel,
  // mobile navigates to a full-screen page. ngOnInit then refines this from the dialog flag.
  isRightSideContent = this.windowService.isDesktopRes;
  errorMessage = '';
  estimate: SteadilyEstimate | null = null;
  embedUrl: SafeResourceUrl | null = null;
  propertyName = '';
  addressLine = '';
  currentProvider = '';
  currentAnnualPremium = 0;

  monthlyPremium = 0;
  annualPremium = 0;
  annualSavings = 0;
  annualPremiumLabel = '';
  savingsLabel = '';
  plans: QuotePlanView[] = [];
  comparisonBars: ComparisonBar[] = [];

  /** Animated count-up value shown in the hero. */
  readonly displayedMonthly = signal(0);
  readonly iframeLoaded = signal(false);
  readonly showEmbedHint = signal(false);

  boundOpenApplication = this.openApplication.bind(this);

  private countUpFrame: number | null = null;
  private embedHintTimeout: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit() {
    try {
      // Only the desktop panel is "right side content". On mobile there is no dialog, so the
      // page must render its own full-screen layout with a back button — never trust a stale
      // dialog flag left in storage from a previous desktop session.
      const dialogOpen = await this.storage.retrieve('dialog-right-side-open') || false;
      this.isRightSideContent = dialogOpen && this.windowService.isDesktopRes;
      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogPageTitle('Insurance Quote');
      }
      // Params arrive via the dialog container's `data` (desktop), router state (mobile),
      // or dialog-params storage (fallback) — in that order of reliability.
      const dialogParams = await this.storage.retrieve('dialog-params');
      const params: Partial<SteadilyQuoteParams> = this.data || {};
      this.estimate = params.estimate || this.navCtrl.getParam('estimate') || dialogParams?.estimate || null;
      this.propertyName = params.propertyName || this.navCtrl.getParam('propertyName') || dialogParams?.propertyName || '';
      this.addressLine = params.addressLine || this.navCtrl.getParam('addressLine') || dialogParams?.addressLine || '';
      this.currentProvider = params.currentProvider || this.navCtrl.getParam('currentProvider') || dialogParams?.currentProvider || '';
      this.currentAnnualPremium = params.currentAnnualPremium || this.navCtrl.getParam('currentAnnualPremium') || dialogParams?.currentAnnualPremium || 0;
      if (this.estimate) {
        this.embedUrl = this.estimate.start_url
          ? this.sanitizer.bypassSecurityTrustResourceUrl(this.estimate.start_url)
          : null;
        this.buildQuoteView(this.estimate);
        this.startCountUp();
        // Only the web renders the inline funnel, so only the web needs the "still loading" hint.
        if (!this.isNativeApp) {
          this.embedHintTimeout = setTimeout(() => {
            if (!this.iframeLoaded()) {
              this.showEmbedHint.set(true);
            }
          }, EMBED_HINT_DELAY_MS);
        }
      }
    } catch (err) {
      console.error('[SteadilyQuote] failed to initialize', err);
      this.errorMessage = err?.message || 'Unable to load this quote.';
      // Drop a partially-built quote so the template falls back to a clean error state
      // rather than rendering the price hero with a broken plan section beneath it.
      this.estimate = null;
    }
    this.loaded = true;
    // The awaited storage reads resolve outside Angular's zone, so this OnPush view
    // needs an explicit check to render the loaded state.
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.countUpFrame !== null) {
      cancelAnimationFrame(this.countUpFrame);
    }
    if (this.embedHintTimeout !== null) {
      clearTimeout(this.embedHintTimeout);
    }
  }

  private buildQuoteView(estimate: SteadilyEstimate): void {
    this.annualPremium = estimate.estimate.lowest;
    this.monthlyPremium = Math.round(this.annualPremium / 12);
    this.annualSavings = this.currentAnnualPremium > 0
      ? Math.max(0, Math.round(this.currentAnnualPremium - this.annualPremium))
      : 0;
    this.annualPremiumLabel = `estimated · $${this.annualPremium.toLocaleString('en-US')}/yr`;
    this.savingsLabel = `Save $${this.annualSavings.toLocaleString('en-US')}/yr vs your current policy`;
    this.plans = (estimate.product_options || []).map((option, index) => ({
      product: option.product,
      name: `${option.product} · ${PLAN_NAMES[option.product]?.name || option.product}`,
      description: PLAN_NAMES[option.product]?.description || '',
      monthly: `$${Math.round(option.premium / 12).toLocaleString('en-US')}/mo`,
      annual: `$${option.premium.toLocaleString('en-US')}/yr`,
      isRecommended: index === 0,
      coverageLines: this.buildCoverageLines(option.coverages),
    }));
    this.buildComparisonBars();
  }

  /**
   * Steadily omits the whole `coverages` object on some estimates and individual fields
   * (e.g. wind & hail) on others, so build the list from what's present. An empty list just
   * renders the plan with its price and no breakdown — never a half-built page and an error.
   */
  private buildCoverageLines(coverages?: SteadilyCoverageDetails): { label: string; value: string }[] {
    if (!coverages) { return []; }
    const dollars = (amount?: number): string | null =>
      amount === null || amount === undefined ? null : `$${amount.toLocaleString('en-US')}`;
    const candidates: { label: string; value?: string | null }[] = [
      { label: 'Loss valuation', value: coverages.loss_valuation },
      { label: 'Dwelling', value: dollars(coverages.dwelling_limit) },
      { label: 'Other structures', value: dollars(coverages.private_structures_limit) },
      { label: 'Personal property', value: dollars(coverages.personal_property_limit) },
      { label: 'Loss of rents', value: dollars(coverages.loss_of_rents_limit) },
      { label: 'Perils deductible', value: coverages.all_other_perils_deductible },
      { label: 'Wind & hail deductible', value: coverages.wind_hail_deductible },
    ];
    return candidates.filter(line => line.value).map(line => ({ label: line.label, value: line.value as string }));
  }

  private buildComparisonBars(): void {
    if (!this.currentAnnualPremium || this.currentAnnualPremium <= 0) {
      this.comparisonBars = [];
      return;
    }
    const max = Math.max(this.currentAnnualPremium, this.annualPremium);
    this.comparisonBars = [
      {
        label: this.currentProvider || 'Current policy',
        amountLabel: `$${Math.round(this.currentAnnualPremium).toLocaleString('en-US')}/yr`,
        widthPct: Math.round((this.currentAnnualPremium / max) * 100),
        tone: 'current',
      },
      {
        label: 'Steadily estimate',
        amountLabel: `$${this.annualPremium.toLocaleString('en-US')}/yr`,
        widthPct: Math.round((this.annualPremium / max) * 100),
        tone: 'steadily',
      },
    ];
  }

  /** Counts the hero premium up from $0 so the number lands with some weight. */
  private startCountUp(): void {
    const target = this.monthlyPremium;
    const prefersReducedMotion = typeof matchMedia === 'function'
      && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || target <= 0) {
      this.displayedMonthly.set(target);
      return;
    }
    const durationMs = 900;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.displayedMonthly.set(Math.round(target * eased));
      if (progress < 1) {
        this.countUpFrame = requestAnimationFrame(tick);
      } else {
        this.countUpFrame = null;
      }
    };
    this.countUpFrame = requestAnimationFrame(tick);
  }

  onIframeLoad(): void {
    this.iframeLoaded.set(true);
    this.showEmbedHint.set(false);
  }

  openApplication(): void {
    if (this.estimate?.start_url) {
      this.util.openUrl(this.estimate.start_url);
    }
  }

  /**
   * Tapping a plan card continues into the prefilled Steadily application. The funnel is the
   * single co-branded entry point (there's no per-plan deep link), so every plan routes to
   * the same place — the point is that the card now responds instead of doing nothing.
   */
  selectPlan(): void {
    this.openApplication();
  }
}
